// =============================================================================
// cardArt.ts — CONFIG CENTRAL das cartas de upgrade (a Urna).
//
// >>> É AQUI QUE VOCÊ MEXE. <<<
// Para cada carta (por id de arma/passiva) você pode definir:
//   • theme      — cor do BANNER do título (hex)
//   • gemTop      — cor da GEMA da crista (topo do card)
//   • gemCorner   — cor das 4 GEMAS de canto
//   • gemL / gemR — cor das gemas ESQUERDA/DIREITA da barra de mecânica
//   • tag         — texto da BARRA DE MECÂNICA (default: o `tipo` da arma /
//                   "passiva" nos upgrades)
//   • title       — TÍTULO no banner (default: o nome do jogo)
//   • desc        — DESCRIÇÃO no painel (default: a descrição do jogo).
//                   O painel é pequeno: escreva descrições CURTAS aqui quando
//                   for caprichar. Descrições longas encaixam e truncam sozinhas.
//   • image       — caminho da ARTE (default: assets/cards/<id>.png)
//
// A ARTE de cada carta é um PNG que você gera depois e joga na pasta
// public/assets/cards/ (veja o README de lá). Enquanto o PNG não existe, o card
// mostra um placeholder xadrez — nada quebra. Todo o resto (moldura, gemas,
// banner) é desenhado por código em cardForge.ts, nenhum PNG necessário.
//
// Paletas de referência (as mesmas da Forja de Cards):
//   temas : verde #3f9e22 · verde-esc #1c5c46 · laranja #c8571a · roxo #6a2fa0
//           vermelho #b02020 · dourado #b8860b · azul #2f6fb8 · ciano #188a8a
//           rosa #c03a7c · grafite #3a3f46
//   gemas : #38b24a #3aa0e8 #e33b3b #a04ae0 #e8c53a #e8763a #2fd6c0 #e04a9a
// =============================================================================

import { UPGRADES, fmtNum, fmtPct } from '../../../shared/upgrades';

export interface CardArt {
  theme?: string;
  gemTop?: string;
  gemCorner?: string;
  gemL?: string;
  gemR?: string;
  tag?: string;
  title?: string;
  desc?: string;
  image?: string;
}

// Pasta onde entram as artes geradas (uma por carta): assets/cards/<id>.png
export const CARDS_ART_DIR = 'assets/cards';
export function cardImagePath(id: string): string {
  return `${CARDS_ART_DIR}/${id}.png`;
}

// Fallbacks quando um campo não está definido no mapa abaixo.
const DEFAULT_WEAPON = { theme: '#2f6fb8', gemTop: '#3aa0e8', gemCorner: '#38b24a', gemL: '#38b24a', gemR: '#3aa0e8' };
const DEFAULT_UPGRADE = { theme: '#6a2fa0', gemTop: '#a04ae0', gemCorner: '#e8c53a', gemL: '#e8c53a', gemR: '#a04ae0' };
// Card DOURADO "Emenda de Relator" (#93): força a paleta de ouro pra sinalizar
// o efeito turbinado, mantendo título/arte/tag da carta original.
const GOLDEN = { theme: '#b8860b', gemTop: '#f6d96a', gemCorner: '#e8c53a', gemL: '#f6d96a', gemR: '#e8c53a' };

// Magnitudes compartilhadas (fonte única da verdade): as descs curtas das
// passivas abaixo interpolam os MESMOS números que as fórmulas do servidor
// leem (shared/upgrades.ts `fx`) — nada de cópia à mão que descola.
const FX = {
  gabinete: UPGRADES.gabinete_odio.fx,
  robos: UPGRADES.robos_whatsapp.fx,
  pec: UPGRADES.pec_vinganca.fx,
  impeach: UPGRADES.impeachment_express.fx,
  auxilio: UPGRADES.auxilio_brasil.fx,
  ze: UPGRADES.ze_gotinha.fx,
  passaporte: UPGRADES.passaporte.fx,
  ima: UPGRADES.ima_votos.fx,
};

// -----------------------------------------------------------------------------
// MAPA DE CARTAS — edite à vontade. Cada id abaixo tem um placeholder de arte
// esperado em assets/cards/<id>.png.
// -----------------------------------------------------------------------------
export const CARD_ART: Record<string, CardArt> = {
  // ---------------- ARMAS iniciais/base ----------------
  live_varanda:       { theme: '#c8571a', gemTop: '#e8763a', gemCorner: '#e8c53a', gemL: '#e8763a', gemR: '#e8c53a', desc: 'Cone de "HIC!" que empurra a horda. Evolui pra soluço triplo.' },
  cachaca_51:         { theme: '#3f9e22', gemTop: '#38b24a', gemCorner: '#e8c53a', gemL: '#38b24a', gemR: '#e8c53a', desc: 'Pão que explode e deixa poça de mortadela escorregadia.' },
  panelaco:           { theme: '#188a8a', gemTop: '#2fd6c0', gemCorner: '#3aa0e8', gemL: '#2fd6c0', gemR: '#3aa0e8', desc: 'Rajada de vento que atravessa a fila e empurra forte.' },
  pombas_da_paz:      { theme: '#1c5c46', gemTop: '#38b24a', gemCorner: '#2fd6c0', gemL: '#38b24a', gemR: '#2fd6c0', desc: 'Raízes prendem os inimigos no lugar e ainda os amolecem.' },
  carteira_trabalho:  { theme: '#2f6fb8', gemTop: '#3aa0e8', gemCorner: '#e8c53a', gemL: '#3aa0e8', gemR: '#e8c53a', desc: 'Bumerangue que assina de novo, com dano dobrado na volta.' },
  cruz_credo:         { theme: '#6a2fa0', gemTop: '#a04ae0', gemCorner: '#e8c53a', gemL: '#a04ae0', gemR: '#e8c53a', desc: 'Cruzes giram coladas em você exorcizando quem encosta.' },
  vampiros_madrugada: { theme: '#b02020', gemTop: '#e33b3b', gemCorner: '#a04ae0', gemL: '#e33b3b', gemR: '#a04ae0', desc: 'Morcegos sugam vida da horda e devolvem cura pra você.' },
  cadeirada:          { theme: '#c8571a', gemTop: '#e8763a', gemCorner: '#e33b3b', gemL: '#e8763a', gemR: '#e33b3b', desc: 'Cadeira que estilhaça no chão e atordoa no impacto.' },
  fake_news:          { theme: '#c03a7c', gemTop: '#e04a9a', gemCorner: '#3aa0e8', gemL: '#e04a9a', gemR: '#3aa0e8', desc: 'Boato teleguiado que se multiplica a cada quique.' },
  lulinha:            { theme: '#b02020', gemTop: '#e33b3b', gemCorner: '#e8c53a', gemL: '#e33b3b', gemR: '#e8c53a', desc: 'Mãozinha ricocheteia e bate mais forte a cada quique.' },
  pix_emendas:        { theme: '#b8860b', gemTop: '#e8c53a', gemCorner: '#38b24a', gemL: '#e8c53a', gemR: '#38b24a', desc: 'Invoca aliados que lutam por você. Fisiologismo aplicado.' },
  cpi_giratoria:      { theme: '#6a2fa0', gemTop: '#a04ae0', gemCorner: '#3aa0e8', gemL: '#a04ae0', gemR: '#3aa0e8', desc: 'Seringas orbitam e imunizam à força quem chega perto.' },
  rachadinha:         { theme: '#188a8a', gemTop: '#2fd6c0', gemCorner: '#e8c53a', gemL: '#2fd6c0', gemR: '#e8c53a', desc: 'Aura de imposto drena a horda em volta e te cura.' },
  mensalao:           { theme: '#c8571a', gemTop: '#e8763a', gemCorner: '#e8c53a', gemL: '#e8763a', gemR: '#e8c53a', desc: 'Leque de dinheiro na cara da horda. Pagamento em espécie.' },
  oito_jan:           { theme: '#b02020', gemTop: '#e33b3b', gemCorner: '#e8763a', gemL: '#e33b3b', gemR: '#e8763a', desc: 'Explosão em volta com empurrão. Quebra tudo, até o relógio.' },
  stf:                { theme: '#2f6fb8', gemTop: '#3aa0e8', gemCorner: '#2fd6c0', gemL: '#3aa0e8', gemR: '#2fd6c0', desc: 'Congela a horda num raio largo. Cuidado: pode te congelar.' },

  // ---------------- ARMAS supremas (evoluções) ----------------
  cadeia_nacional:      { theme: '#c8571a', gemTop: '#f6d96a', gemCorner: '#e8763a', gemL: '#f6d96a', gemR: '#e8763a', desc: '5 rajadas de "HIC!" aleatórias que empurram e atordoam.' },
  refinaria_garapa:     { theme: '#3f9e22', gemTop: '#e8c53a', gemCorner: '#38b24a', gemL: '#e8c53a', gemR: '#38b24a', desc: 'Chove pão: poças gigantes de gordura com sucção.' },
  usina_angra:          { theme: '#188a8a', gemTop: '#2fd6c0', gemCorner: '#e8c53a', gemL: '#2fd6c0', gemR: '#e8c53a', desc: '2 tornados errantes puxam a horda e moem sem parar.' },
  revoada_cop30:        { theme: '#1c5c46', gemTop: '#2fd6c0', gemCorner: '#38b24a', gemL: '#2fd6c0', gemR: '#38b24a', desc: 'Prende multidões e brota 3 árvores-barreira.' },
  mei_supremo:          { theme: '#2f6fb8', gemTop: '#f6d96a', gemCorner: '#3aa0e8', gemL: '#f6d96a', gemR: '#3aa0e8', desc: 'Enxame de 6 carteiras cruzando o país inteiro.' },
  concordata_divina:    { theme: '#6a2fa0', gemTop: '#f6d96a', gemCorner: '#a04ae0', gemL: '#f6d96a', gemR: '#a04ae0', desc: 'Dois anéis de cruzes empurram e refletem projéteis.' },
  reforma_previdencia:  { theme: '#b02020', gemTop: '#a04ae0', gemCorner: '#e33b3b', gemL: '#a04ae0', gemR: '#e33b3b', desc: '6 vampiros de terno emendam mordidas e te curam.' },
  brasil_urgente:       { theme: '#c8571a', gemTop: '#e33b3b', gemCorner: '#e8763a', gemL: '#e33b3b', gemR: '#e8763a', desc: '4 cadeiras por leva forram o chão de lascas.' },
  deep_fake:            { theme: '#c03a7c', gemTop: '#2fd6c0', gemCorner: '#e04a9a', gemL: '#2fd6c0', gemR: '#e04a9a', desc: 'Projéteis com IA que se dividem duas vezes.' },

  // ---------------- PASSIVAS (upgrades) ----------------
  gabinete_odio:      { theme: '#3f9e22', gemTop: '#38b24a', gemCorner: '#e8c53a', gemL: '#38b24a', gemR: '#e8c53a', tag: 'passiva', desc: `Todas as armas recarregam ${fmtPct(FX.gabinete.cdRedux)}% mais rápido. Acumula 5x.` },
  robos_whatsapp:     { theme: '#1c5c46', gemTop: '#38b24a', gemCorner: '#2fd6c0', gemL: '#38b24a', gemR: '#2fd6c0', tag: 'passiva', desc: `+1 aliado invocável e +${fmtPct(FX.robos.allyDmg)}% de dano de toda a tropa.` },
  foro_privilegiado:  { theme: '#b8860b', gemTop: '#f6d96a', gemCorner: '#e8c53a', gemL: '#f6d96a', gemR: '#e8c53a', tag: 'passiva', desc: 'Fica IMUNE de tempos em tempos. A lei é pra todos*.' },
  pec_vinganca:       { theme: '#b02020', gemTop: '#e33b3b', gemCorner: '#e8763a', gemL: '#e33b3b', gemR: '#e8763a', tag: 'passiva', desc: `+${fmtPct(FX.pec.dmg)}% de dano em todas as armas. Manchete de domingo.` },
  impeachment_express:{ theme: '#3a3f46', gemTop: '#3aa0e8', gemCorner: '#e33b3b', gemL: '#3aa0e8', gemR: '#e33b3b', tag: 'consumível', desc: `Limpa a onda atual, mas dobra o spawn por ${fmtNum(FX.impeach.chaosDur)}s.` },
  auxilio_brasil:     { theme: '#2f6fb8', gemTop: '#e8c53a', gemCorner: '#38b24a', gemL: '#e8c53a', gemR: '#38b24a', tag: 'passiva', desc: `+${fmtNum(FX.auxilio.hp)} de HP máximo e cura na hora. Perto da eleição.` },
  ze_gotinha:         { theme: '#188a8a', gemTop: '#2fd6c0', gemCorner: '#38b24a', gemL: '#2fd6c0', gemR: '#38b24a', tag: 'passiva', desc: `Regenera +${fmtNum(FX.ze.regen)} de HP por segundo. Ciência funcionando.` },
  passaporte:         { theme: '#2f6fb8', gemTop: '#3aa0e8', gemCorner: '#e8c53a', gemL: '#3aa0e8', gemR: '#e8c53a', tag: 'passiva', desc: `+${fmtPct(FX.passaporte.speed)}% de velocidade. Ideal pra "férias" na Flórida.` },
  ima_votos:          { theme: '#c03a7c', gemTop: '#e04a9a', gemCorner: '#e8c53a', gemL: '#e04a9a', gemR: '#e8c53a', tag: 'passiva', desc: `+${fmtPct(FX.ima.pickup)}% de raio de coleta de votos. O algoritmo entrega.` },
};

// -----------------------------------------------------------------------------
// Resolução: junta os defaults + o override por id + os dados do jogo (nome,
// desc, tipo). O que sai daqui já vai pronto pro renderizador (cardForge.ts).
// -----------------------------------------------------------------------------
export interface ResolvedCardArt {
  theme: string;
  gemTop: string;
  gemCorner: string;
  gemL: string;
  gemR: string;
  tag: string;
  title: string;
  desc: string;
  image: string;
}

export function resolveCardArt(opts: {
  kind: 'weapon' | 'upgrade';
  id: string;
  nome: string;
  desc: string;
  tipo?: string;         // `tipo` da arma (vira a tag default)
  golden?: boolean;      // card dourado "Emenda de Relator"
}): ResolvedCardArt {
  const over = CARD_ART[opts.id] ?? {};
  const kindBase = opts.kind === 'weapon' ? DEFAULT_WEAPON : DEFAULT_UPGRADE;
  const defaultTag = opts.tipo ?? (opts.kind === 'upgrade' ? 'passiva' : '');
  // Dourado força a paleta de ouro (sinal do efeito turbinado); tag, título e
  // arte continuam da carta original.
  if (opts.golden) {
    return {
      ...GOLDEN,
      tag: over.tag ?? defaultTag,
      title: over.title ?? opts.nome,
      desc: over.desc ?? opts.desc,
      image: over.image ?? cardImagePath(opts.id),
    };
  }
  return {
    theme: over.theme ?? kindBase.theme,
    gemTop: over.gemTop ?? kindBase.gemTop,
    gemCorner: over.gemCorner ?? kindBase.gemCorner,
    gemL: over.gemL ?? kindBase.gemL,
    gemR: over.gemR ?? kindBase.gemR,
    tag: over.tag ?? defaultTag,
    title: over.title ?? opts.nome,
    desc: over.desc ?? opts.desc,
    image: over.image ?? cardImagePath(opts.id),
  };
}
